import { makeAutoObservable, computed } from 'mobx';
import {
  Label,
  Milestone,
  RawProject,
  RawMilestone,
  RawTaskGroup,
  ProjectProgress,
  RawProjectFile,
  Icon,
  VercelProject,
  RawTaskActivityLogInterface,
  TaskActivityLogInterface
} from '../interfaces/project';
import { rootStore } from '../rootStore';
import { ProjectResourceDetailInterface, StatusListInterface } from '../../interfaces/ProjectInterface';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import { determineProjectStatus, getDefaultProgress } from '../../services/projectServices';
import { NewTaskFilterInterface } from '../../interfaces/TaskInterface';
import { applyTaskFilter, filterCompletedTasksByDate, getWeekTasks } from '../../services/mobxTaskFilterService';
import { isEmpty, mathRound } from '../../helpers/common';
import {
  FILE_ORDERING_TYPE,
  DEFAULT_TASK_DISPLAY_PROPERTIES,
  PROJECT_STATUS_TEXT,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE,
  TaskFilterPropertyType
} from '../../global/constants';
import { convertRawTaskActivityLog, defaultDisplayFilters, formatToIcon } from '../common';

export class ProjectModel {
  // Common
  id: string;
  name: string;
  description: string | null;
  identifier: string | null;

  // Dates
  updatedOn: Date;
  createdOn: Date;
  dueDate: Date | null;

  // Relational
  createdById: string;
  customerId: string;
  companyId: string;
  usersIds: string[];

  //Filter
  taskFilterType: NewTaskFilterInterface;
  projectFilterType: NewTaskFilterInterface;
  listItemsDisplayFilter: Partial<Record<TaskFilterPropertyType, boolean>> & { [key: string]: boolean };

  // Others
  index: number;
  isArchived: boolean;
  isEstimate: boolean;
  isFavorite: boolean;
  isGroupEnabled: boolean;
  // isPriority: boolean;
  isMilestone: boolean;
  labelsEnabled: boolean;
  labelsList: Label[];
  multiAssignee: boolean;
  priority: {
    enabled: boolean;
    default: number;
  };
  statusData: StatusListInterface[];
  statusEnable: boolean;
  customer: CustomerDetailsInterface | null;
  githubRepository: number | null;
  resources: ProjectResourceDetailInterface[];
  milestones: Milestone[] = [];
  taskGroups: RawTaskGroup[] = [];
  startOfTheWeek: Date = new Date();
  files: RawProjectFile[] = [];
  currentFolderId: string | null = null;
  archiveMilestoneList: Milestone[] = [];
  icon: Icon | null = null;
  vercelProject?: VercelProject | null;
  activityLogs: TaskActivityLogInterface[] = [];
  fileOrdering: number;

  constructor(rawProject: RawProject) {
    makeAutoObservable(this, {
      displayPropertiesFilterWithFirebase: computed,
      filteredProjectTasks: computed,
      filteredTask: computed,
      usersWithWorkload: computed,
      calculateUnassignedTasksPercentage: computed,
      weekWiseTasks: computed,
      folderWiseFiles: computed
    });
    // Common
    this.id = rawProject.id;
    this.name = rawProject.name;
    this.description = rawProject.description;
    this.identifier = rawProject.identifier || rawProject.Identifier || null;
    this.createdOn = new Date(rawProject.createdOn);
    this.updatedOn = new Date(rawProject.updatedOn);
    this.dueDate = rawProject.dueDate ? new Date(rawProject.dueDate) : null;

    //Filter
    this.taskFilterType = {
      showSubTask: rawProject.taskFilter?.showSubTask ?? false,
      showCompletedTaskByFilter:
        rawProject.taskFilter?.showCompletedTaskByFilter ?? SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH,
      showRecentlyCompletedTask: rawProject.taskFilter?.showRecentlyCompletedTask ?? true,
      showEmptyGroup: rawProject.taskFilter?.showEmptyGroup ?? true,
      taskViewType: rawProject.taskFilter?.taskViewType ?? TASK_LIST_TYPE.TASKS,
      groupingStatus: rawProject.taskFilter?.groupingStatus ?? SHOW_TASK_GROUPING_TYPE.DEFAULT,
      orderByStatus: rawProject.taskFilter?.orderByStatus ?? SHOW_TASK_BY_ORDER.CUSTOM,
      displayProperties: rawProject.taskFilter?.displayProperties ?? DEFAULT_TASK_DISPLAY_PROPERTIES
    };
    this.projectFilterType = {
      ...defaultDisplayFilters,
      ...(rawProject.taskFilter || {})
    };
    this.listItemsDisplayFilter = rawProject.listItemsDisplayFilter || {};

    // const rawListItemsDisplayFilter = rawProject.listItemsDisplayFilter;

    // this.listItemsDisplayFilter =
    //   rawListItemsDisplayFilter && !isEmpty(rawListItemsDisplayFilter)
    //     ? { ...defaultListItemsDisplayFilter, ...(rawListItemsDisplayFilter || {}) }
    //     : { ...defaultListItemsDisplayFilter };

    // Initialize file ordering from raw project data or default to DATE_UPLOADED
    this.fileOrdering = rawProject.taskFilter?.fileOrdering ?? FILE_ORDERING_TYPE.DATE_UPLOADED;

    // Relational
    this.createdById = rawProject.createdBy;
    this.customerId = rawProject.customerId;
    this.companyId = rawProject.companyId;
    this.usersIds = rawProject.users;

    // Others
    this.index = this.createdOn.getTime();
    this.isArchived = rawProject.isArchived ?? false;
    this.isEstimate = rawProject.isEstimate ?? false;
    this.isFavorite = rawProject.isFavorite ?? false;
    this.isGroupEnabled = rawProject.isGroupEnabled ?? false;
    // this.isPriority = rawProject.isPriority ?? false;
    this.isMilestone = rawProject.isMilestone ?? false;
    this.labelsEnabled = rawProject.labelsEnabled || false;
    this.labelsList = rawProject.labelsList || [];
    this.multiAssignee = rawProject.multiAsignee || false;
    this.priority = rawProject.priority ?? { enabled: false, default: 0 };
    this.statusData = rawProject.status ?? [];
    this.statusEnable = rawProject.statusEnable ?? false;
    // this.taskGroups = rawProject.taskGroups ?? [];
    // this.milestones = rawProject.milestones ?? [];
    this.customer = rawProject.customer ?? null;
    this.githubRepository = rawProject.githubRepository ?? null;
    this.resources = rawProject.resources ?? [];
    this.currentFolderId = null;
    this.icon = formatToIcon(rawProject.name, rawProject.icon);
    this.vercelProject = rawProject.vercelProject ?? null;
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.usersIds.includes(user.id)) || [];
  }

  get createdBy() {
    return rootStore.workspaceStore.currentWorkspace?.users.find((user) => user.id === this.createdById) || null;
  }

  get defaultStatus() {
    return this.statusData.find((status) => status.Default) || this.statusData[0] || {};
  }

  get tasks() {
    // slice() so consumers can sort/mutate without corrupting the shared memoized index.
    return rootStore.taskStore.tasksByProjectId.get(this.id)?.slice() ?? [];
  }

  get usersWithWorkload() {
    const openTasks = this.tasks.filter((task) => !task.status);
    const openTasksCount = openTasks.length;
    const countByUserId = new Map<string, number>();
    openTasks.forEach((task) => {
      task.usersIds.forEach((userId) => countByUserId.set(userId, (countByUserId.get(userId) ?? 0) + 1));
    });

    return this.users
      .map((user) => ({
        ...user,
        workload: openTasksCount === 0 ? 0 : ((countByUserId.get(user.id) ?? 0) / openTasksCount) * 100
      }))
      .filter((user) => user.workload > 0)
      .toSorted((a, b) => b.workload - a.workload);
  }

  get calculateUnassignedTasksPercentage() {
    const openTasks = this.tasks.filter((task) => !task.status);
    const unassignedOpenTasksCount = openTasks.filter((task) => task.usersIds.length === 0).length;
    const unassignedTasksPercentage = openTasks.length === 0 ? 0 : (unassignedOpenTasksCount / openTasks.length) * 100;
    return unassignedTasksPercentage;
  }

  get filteredTask() {
    return this.tasks.filter((task) => !task.parentTaskId);
  }

  get progressData(): ProjectProgress {
    if (this.tasks.length === 0) {
      return getDefaultProgress();
    }

    const completedTasks = this.tasks.filter((x: { status: boolean }) => x?.status)?.length || 0;
    const progress = this.tasks.length === 0 ? 0 : Number(mathRound((completedTasks / this.tasks.length) * 100, 0));
    const status = determineProjectStatus(this.tasks);

    return {
      progress,
      completedTasks,
      totalTasks: this.tasks.length,
      status,
      statusText: PROJECT_STATUS_TEXT[status]
    };
  }

  get filteredProjectTasks() {
    const parentTasks = filterCompletedTasksByDate(this);
    return applyTaskFilter(parentTasks, this);
  }

  get weekWiseTasks() {
    // No week-range pre-filter here: getWeekTasks() buckets by exact day against a Monday-start week,
    // and a dayjs Sunday-start range would drop the Sunday column's tasks before they reach it.
    return getWeekTasks(this.startOfTheWeek, this.tasks, this.taskFilterType) || [];
  }

  get folderWiseFiles() {
    let filteredFiles;

    if (this.currentFolderId) {
      filteredFiles = this.files.filter((file) => file.parentId === this.currentFolderId);
    } else {
      filteredFiles = this.files.filter((file) => !file.parentId);
    }

    // Apply sorting based on file ordering preference
    const orderingType = this.fileOrdering;

    switch (orderingType) {
      case FILE_ORDERING_TYPE.NAME:
        return filteredFiles.toSorted((a, b) => a.name.localeCompare(b.name));
      case FILE_ORDERING_TYPE.DATE_UPLOADED:
        return filteredFiles.toSorted((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
      case FILE_ORDERING_TYPE.DATE_MODIFIED:
        // Since we don't have updatedOn field, we'll use createdOn for both date uploaded and modified
        return filteredFiles.toSorted((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
      case FILE_ORDERING_TYPE.UPLOADED_BY:
        return filteredFiles.toSorted((a, b) => {
          const aUser = a.user?.name || 'Unknown';
          const bUser = b.user?.name || 'Unknown';
          return aUser.localeCompare(bUser);
        });
      default:
        return filteredFiles.toSorted((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
    }
  }

  get displayPropertiesFilterWithFirebase(): Partial<Record<TaskFilterPropertyType, boolean>> {
    const hasActiveDisplayFilter = !isEmpty(this.listItemsDisplayFilter);
    if (hasActiveDisplayFilter) {
      return {
        ...(DEFAULT_TASK_DISPLAY_PROPERTIES || {}),
        ...(this.taskFilterType?.displayProperties || {}),
        ...(this.listItemsDisplayFilter || {})
      };
    }
    return {
      ...(DEFAULT_TASK_DISPLAY_PROPERTIES || {}),
      ...(this.taskFilterType?.displayProperties || {})
    };
  }

  setIdentifier(identifier: string) {
    this.identifier = identifier.toUpperCase();
  }

  setName(name: string) {
    this.name = name;
  }

  setStartOfTheWeek(startOfTheWeek: Date) {
    this.startOfTheWeek = startOfTheWeek;
  }

  setFavorite(isFavorite: boolean) {
    this.isFavorite = isFavorite;
  }

  setIndex(index: number) {
    this.index = index;
  }

  addUser(userId: string) {
    this.usersIds.push(userId);
  }

  setLabel(label: Label[]) {
    this.labelsList = label;
  }

  removeUser(userId: string) {
    this.usersIds = this.usersIds.filter((id) => id !== userId);
  }

  addResource(resource: ProjectResourceDetailInterface) {
    this.resources.push(resource);
  }

  removeResource(resourceId: string) {
    this.resources = this.resources.filter((r) => r['_id'] !== resourceId);
  }

  updateResource(resourceId: string, resourceUpdate: { reference?: string; label?: string }) {
    this.resources = this.resources.map((r) => {
      if (r['_id'] === resourceId) {
        const Label = resourceUpdate.label ?? r.Label;
        const Reference = resourceUpdate.reference ?? r.Reference;
        return { ...r, Label, Reference };
      }
      return r;
    });
  }

  setGroupEnabled(isGroupEnabled: boolean) {
    this.isGroupEnabled = isGroupEnabled;
  }

  setPriorityEnabled(priorityEnabled: boolean) {
    this.priority.enabled = priorityEnabled;
  }

  setDefaultPriority(defaultPriority: number) {
    this.priority.default = defaultPriority;
  }

  setStatusEnabled(statusEnabled: boolean) {
    this.statusEnable = statusEnabled;
  }

  setLabelsEnabled(labelsEnabled: boolean) {
    this.labelsEnabled = labelsEnabled;
  }

  setMultiAssignee(multiAssignee: boolean) {
    this.multiAssignee = multiAssignee;
  }

  setEstimate(isEstimate: boolean) {
    this.isEstimate = isEstimate;
  }

  setMilestone(isMilestone: boolean) {
    this.isMilestone = isMilestone;
  }

  setGithubRepository(githubRepository: number) {
    this.githubRepository = githubRepository;
  }

  setTaskFilterType(filterType: NewTaskFilterInterface) {
    this.taskFilterType = filterType;
  }

  setListItemsDisplayFilter(filterType: { [key: number]: boolean }) {
    this.listItemsDisplayFilter = filterType;
  }

  updateTaskFilterType<K extends keyof NewTaskFilterInterface>(propsName: K, value: NewTaskFilterInterface[K]) {
    this.taskFilterType[propsName] = value;
  }

  updateProjectFilterType<K extends keyof NewTaskFilterInterface>(propsName: K, value: NewTaskFilterInterface[K]) {
    this.projectFilterType[propsName] = value;
  }

  updateListItemsDisplayFilter(propertyType: string, isVisible: boolean) {
    this.listItemsDisplayFilter[propertyType] = isVisible;
  }

  setMilestonesList(milestones: RawMilestone[]) {
    this.milestones = milestones
      .map((milestone) => {
        const tasks = this.tasks
          .filter((task) => task.milestoneId === milestone.id)
          .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
        // Get unique user IDs from tasks using Set and flatMap
        const userIDs = Array.from(new Set(tasks.flatMap((task) => task.usersIds || [])));
        const users = this.users.filter((user) => userIDs.includes(user.id));
        let progressData = getDefaultProgress();
        if (tasks.length > 0) {
          const completedTasks = tasks.filter((x: { status: boolean }) => x?.status)?.length || 0;
          const progress = tasks.length === 0 ? 0 : Number(mathRound((completedTasks / tasks.length) * 100, 0));
          const status = determineProjectStatus(tasks);
          progressData = {
            progress,
            completedTasks,
            totalTasks: tasks.length,
            status,
            statusText: PROJECT_STATUS_TEXT[status]
          };
        }
        return {
          ...milestone,
          tasks,
          users,
          progressData
        };
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  setArchiveMilestoneList(archiveMilestoneList: RawMilestone[]) {
    this.archiveMilestoneList = archiveMilestoneList
      .map((milestone) => {
        const tasks = this.tasks.filter((task) => task.milestoneId === milestone.id);
        // Get unique user IDs from tasks using Set and flatMap
        const userIDs = Array.from(new Set(tasks.flatMap((task) => task.usersIds || [])));
        const users = this.users.filter((user) => userIDs.includes(user.id));
        return {
          ...milestone,
          tasks,
          users,
          progressData: getDefaultProgress()
        };
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  updateMilestone(milestoneId: string, updatedMilestone: Milestone) {
    this.milestones = this.milestones
      .map((milestone) => {
        if (milestone.id === milestoneId) {
          milestone = updatedMilestone;
        }
        return milestone;
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  setStatusData(statusData: StatusListInterface[]) {
    this.statusData = statusData;
  }

  archiveMilestone(milestoneId: string) {
    const milestone = this.milestones.find((milestone) => milestone.id === milestoneId);
    if (milestone) {
      this.milestones = this.milestones.filter((milestone) => milestone.id !== milestoneId);
      this.archiveMilestoneList.push(milestone);
    }
  }

  setTaskGroups(taskGroups: RawTaskGroup[]) {
    this.taskGroups = taskGroups;
  }

  updateProjectFilter() {
    const filter = rootStore.projectStore.getProjectFilter(this.id);
    if (filter) {
      this.taskFilterType = filter;
    }
  }

  setCurrentFolderId(currentFolderId: string | null) {
    this.currentFolderId = currentFolderId;
  }

  setFileOrdering(fileOrdering: number) {
    this.fileOrdering = fileOrdering;
  }

  setProjectFiles(files: RawProjectFile[]) {
    this.files = files;
  }

  setIcon(icon: Icon) {
    this.icon = icon;
  }

  setVercelProject(project: VercelProject) {
    this.vercelProject = project;
  }

  setActivityLogs(activityLogs: RawTaskActivityLogInterface[]) {
    this.activityLogs = activityLogs.map(convertRawTaskActivityLog);
  }

  appendActivityLogs(activityLogs: RawTaskActivityLogInterface[]) {
    const converted = activityLogs.map(convertRawTaskActivityLog);
    this.activityLogs = [...this.activityLogs, ...converted];
  }
}

export const createObservableProject = (rawProject: RawProject) => {
  return new ProjectModel(rawProject);
};
